import { useState } from 'react';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import { ChatGroupInterface } from '@/util/axios/types';

type Props = {
  chatGroup: ChatGroupInterface;
  hideModal: () => void;
  onConfirm: (reason: string) => void;
  loading?: boolean;
};

const REASON_MAX_CHARS = 280;

const CloseChatModal = ({
  chatGroup,
  hideModal,
  onConfirm,
  loading = false,
}: Props) => {
  const [reason, setReason] = useState('');

  // Worker-initiated "Gravy Staff" DMs add every tenant admin as a participant.
  // Closing affects every admin on the thread — surface that in the modal so
  // there's no surprise. An admin-initiated DM has exactly 2 members
  // (admin + worker); anything higher means the chat is shared.
  const isSharedWithOtherAdmins = (chatGroup?.participantCount ?? 0) > 2;

  return (
    <Modal
      displayFlex
      size="xs"
      title={`Close chat with ${chatGroup?.name || 'this worker'}?`}
      onRequestClose={hideModal}
      wrapperBackground={true}
    >
      <Card>
        <Card.Section>
          <Stack vertical gap={16}>
            <Body>
              The worker won&apos;t be able to send new messages here. They can
              still read the chat history. To start a new conversation,
              they&apos;d need to reply to a new announcement.
            </Body>
            {isSharedWithOtherAdmins && (
              <Small color="warning">
                ⚠️ This chat is shared with other admins. Closing it will close
                it for everyone.
              </Small>
            )}
            <Stack vertical gap={4}>
              <Small>Optional note (visible to admins only)</Small>
              <textarea
                id="close-chat-reason"
                value={reason}
                maxLength={REASON_MAX_CHARS}
                placeholder="e.g. Resolved offline"
                rows={3}
                onChange={(e) => setReason(e.target.value)}
                style={{
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  fontSize: '14px',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #d4d6da',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              />
              <Small color="lighter">
                {reason.length}/{REASON_MAX_CHARS}
              </Small>
            </Stack>
            <Stack justify="end" gap={12}>
              <Button
                a11yLabel="Cancel"
                appearance="outline"
                label="Cancel"
                onClick={hideModal}
                disabled={loading}
              />
              <Button
                a11yLabel="Close chat"
                appearance="primary"
                label={loading ? 'Closing…' : 'Close chat'}
                onClick={() => onConfirm(reason.trim())}
                disabled={loading}
              />
            </Stack>
          </Stack>
        </Card.Section>
      </Card>
    </Modal>
  );
};

export default CloseChatModal;
